/**
 * Payment History Page — list of all payments with status badges.
 */

import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Crown, Trophy, Gift, ArrowDownToLine } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { usePaymentHistory } from '@turnifly/shared/hooks'
import { useAuthStore } from '@turnifly/shared/stores'
import { Badge } from '@turnifly/shared/ui'
import { LoadingSpinner } from '@turnifly/shared/ui'
import { format } from 'date-fns'
import type { PaymentHistoryItem } from '@turnifly/shared/api'

interface PaymentHistoryPageProps {
  isOpen: boolean
  onClose: () => void
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
  premium: <Crown size={16} className="text-amber-400" />,
  challenge: <Trophy size={16} className="text-primary" />,
  gift: <Gift size={16} className="text-accent" />,
  referral_payout: <ArrowDownToLine size={16} className="text-success" />,
}

const STATUS_VARIANTS: Record<string, 'success' | 'warning' | 'danger' | 'primary'> = {
  completed: 'success',
  pending: 'warning',
  failed: 'danger',
  refunded: 'primary',
}

export const PaymentHistoryPage: React.FC<PaymentHistoryPageProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { data: payments, isLoading } = usePaymentHistory(isOpen ? user?.id : undefined)

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ y: '100%', opacity: 0.8 }} animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0.8 }}
            transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
            className="fixed z-50 bg-bg flex flex-col inset-x-0 bottom-0 rounded-t-[32px] max-h-[92vh] lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:rounded-3xl lg:max-h-[85vh] lg:w-full lg:max-w-md lg:border lg:border-white/[0.06] lg:shadow-glass"
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-2 shrink-0">
              <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/[0.04] tap-highlight-transparent">
                <X size={20} className="text-text" />
              </button>
              <h2 className="font-display font-bold text-lg text-text">{t('payments.title')}</h2>
              <div className="w-10" />
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-8">
              {isLoading ? (
                <div className="flex justify-center py-8"><LoadingSpinner size="md" /></div>
              ) : !payments?.length ? (
                <div className="py-12 text-center text-text-hint text-sm">{t('payments.noPayments')}</div>
              ) : (
                <div className="space-y-2">
                  {payments.map((p) => <PaymentRow key={p.id} payment={p} />)}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

const PaymentRow: React.FC<{ payment: PaymentHistoryItem }> = React.memo(({ payment }) => {
  const { t } = useTranslation()

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.04]">
      <div className="w-10 h-10 rounded-xl bg-white/[0.04] flex items-center justify-center shrink-0">
        {TYPE_ICONS[payment.type] ?? <Crown size={16} className="text-text-hint" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-text">
            {t(`payments.type.${payment.type}`, payment.type)}
          </span>
          <span className="text-sm font-display font-bold text-text">
            {payment.amount.toLocaleString()} {payment.currency}
          </span>
        </div>
        <div className="flex items-center justify-between mt-1">
          <span className="text-[10px] text-text-hint">
            {format(new Date(payment.created_at), 'dd.MM.yyyy HH:mm')}
          </span>
          <Badge variant={STATUS_VARIANTS[payment.status] ?? 'primary'} className="text-[9px]">
            {t(`payments.status.${payment.status}`, payment.status)}
          </Badge>
        </div>
      </div>
    </div>
  )
})
PaymentRow.displayName = 'PaymentRow'
